import React, { CSSProperties } from 'react';
import { useReduxState } from 'hooks';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { Badge, FlagIcon, MoreOptions, ProgressBar } from 'components/common';
import { CourseViewVersion } from 'components/courses/common';
import { CompletionStatus, CourseProgressTooltip } from '.';

import type { Course, CourseProgress } from 'shared-types';

type Props = {
  id: number | string;
  course?: Pick<Course, 'language' | 'languageVersions'>;
  notStartedLabel?: string;
  progressData: CourseProgress;
  showNotStarted?: boolean;
  showProgressTextLabel?: boolean;
  style?: CSSProperties;
} & (
  | {
      courseSlug: string;
      showCompletedVersionPreview: boolean;
    }
  | {
      courseSlug?: never;
      showCompletedVersionPreview?: never;
    }
);

const CourseProgressStatus = ({
  courseSlug,
  id,
  course,
  notStartedLabel,
  progressData,
  showCompletedVersionPreview = false,
  showNotStarted = true,
  showProgressTextLabel = false,
  style = {}
}: Props) => {
  const { messages, serverConfig } = useReduxState();

  const {
    completion,
    numberOfTasks,
    numberOfVideos,
    readingProgress,
    taskAnswers,
    taskMaxPoints,
    taskPoints,
    taskPointsProgress,
    videoProgress,
    videoProgressVideosWatched
  } = progressData;

  let totalProgress = readingProgress;
  let numberOfProgresses = 1;

  if (numberOfTasks && taskAnswers !== undefined) {
    totalProgress += taskAnswers / numberOfTasks;
    numberOfProgresses++;
  }

  // calculate video completion depending on if individual or combined video requirement type is enabled
  let videoCompletion: number;
  if (serverConfig.videoRequirementsType === 'individual') {
    videoCompletion = videoProgressVideosWatched / numberOfVideos;
  } else {
    videoCompletion = videoProgress;
  }

  if (numberOfVideos && videoCompletion !== undefined) {
    totalProgress += videoCompletion;
    numberOfProgresses++;
  }

  if (numberOfProgresses) {
    totalProgress = totalProgress / numberOfProgresses;
  }

  const showLanguageVersion =
    progressData?.completion?.courseLanguageVersion &&
    Object.keys(course?.languageVersions || {}).length > 0;

  return (
    <div className="status" style={{ ...style }}>
      {completion &&
      !completion?.inactive &&
      completion?.status !== 'inprogress' ? (
        <div className="wrapper">
          <CompletionStatus
            completion={completion}
            course={course}
            discrepancyInCompletion={Boolean(
              progressData?.discrepancyInCompletion
            )}
            progressData={progressData}
            showProgressData={true}
            showTextShort={true}
          />

          {showCompletedVersionPreview && completion?.courseVersion ? (
            <MoreOptions
              buttonStyle={{
                borderRadius: 6,
                height: 25,
                margin: 0,
                padding: '4px 0',
                width: 25
              }}
              category="lighter"
              styleContentButtons={false}
            >
              <CourseViewVersion
                completionId={completion.id}
                courseSlug={courseSlug}
                courseVersion={completion.courseVersion as string}
              />
            </MoreOptions>
          ) : null}
        </div>
      ) : completion?.status === 'inprogress' || totalProgress ? (
        <>
          <CourseProgressTooltip
            attribute={`data-course-progress-status-${id}`}
            discrepancyInCompletion={progressData?.discrepancyInCompletion}
            numberOfTasks={numberOfTasks}
            numberOfVideos={numberOfVideos}
            readingProgress={readingProgress}
            showAnswers={true}
            showReading={true}
            showTaskPoints={true}
            showVideos={true}
            taskAnswers={taskAnswers}
            taskMaxPoints={taskMaxPoints}
            taskPoints={taskPoints}
            taskPointsProgress={taskPointsProgress}
            videoProgress={videoCompletion}
            videosWatched={videoProgressVideosWatched}
          />
          <Badge
            isGroupedFirst={showLanguageVersion}
            isRounded={false}
            size="medium"
            text={
              <div style={{ display: 'inline-flex', flexDirection: 'row' }}>
                <div>
                  {showProgressTextLabel ? `${messages.commonProgress} ` : ''}
                  {`${Math.round(totalProgress * 100)}%`}
                </div>
                <ProgressBar
                  color="#555"
                  progress={totalProgress || 0}
                  style={{ margin: '5px 0 5px 8px', width: 60 }}
                />
              </div>
            }
            {...{ [`data-course-progress-status-${id}`]: true }}
          />

          {showLanguageVersion &&
            (() => {
              const languageOptions = getAllLanguageOptionsByLangCode();
              const languageOption =
                languageOptions[
                  progressData?.completion?.courseLanguageVersion
                ];

              return (
                <Badge
                  isGroupedLast={true}
                  isRounded={false}
                  size="medium"
                  style={{
                    marginLeft: 2,
                    paddingLeft: 5,
                    paddingRight: 5
                  }}
                  text={
                    <div style={{ height: 17 }}>
                      <FlagIcon
                        lang={progressData?.completion?.courseLanguageVersion}
                        size="small"
                        style={{ padding: '1px 0' }}
                      />
                    </div>
                  }
                  tooltip={`${messages.courseLanguageVersion}: ${
                    languageOption?.capitalizedName ||
                    progressData?.completion?.courseLanguageVersion
                  }`}
                />
              );
            })()}
        </>
      ) : showNotStarted ? (
        <Badge
          isRounded={false}
          size="medium"
          text={notStartedLabel || messages.commonNotStarted}
        />
      ) : null}

      <style jsx>{`
        .status {
          white-space: nowrap;
        }
        .wrapper {
          display: flex;
          gap: 5px;
        }
      `}</style>
    </div>
  );
};

export default CourseProgressStatus;
